import React, { useEffect } from 'react';
import moment from 'moment';
import { isEmpty, groupBy, find } from 'lodash';
import styled from 'styled-components/macro';
import { withStyles } from '@material-ui/styles';
import BusinessIcon from '@mui/icons-material/Business';
import RoomIcon from '@mui/icons-material/Room';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import MuiDialogActions from '@material-ui/core/DialogActions';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

import { siteNameGenerator } from 'helpers/site-name-generator';
import TextField from '@mui/material/TextField';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDatePicker from '@mui/lab/StaticDatePicker';
import { FlexCenterVertical } from 'styles/flexbox';
import { getClinicSlots } from 'api/clinic';
import Button from 'components/ui/Button';
import { brand, brandLight, grey50, white } from 'variables/colors';
import { index10 } from 'variables/z-index';
import { border, borderRadius, boxShadow } from 'variables/general';
import GhsLogo from '../../../../assets/images/logo-ghs-full-width.svg';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  height: 100%;
  margin-bottom: auto;
  padding-bottom: 5em;
  overflow-y: auto;
  background-color: ${white};
  border-radius: ${borderRadius};
  box-shadow: ${boxShadow};

  @media (min-width: 30em) {
    max-width: 35em;
    max-height: 50em;
    margin-bottom: 0;
    padding-bottom: 0;
    overflow: hidden;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const OrganizationLogo = styled.img`
  max-width: 12em;
  height: 4em;
  margin: 2em 0 3em;
`;

const Logo = styled.img`
  position: absolute;
  top: 3em;
  left: 3em;
  display: none;
  width: 13em;

  @media (min-width: 30em) {
    display: block;
  }
`;

const NextButton = styled(Button)`
  margin-left: auto;
`;

const Details = styled.div`
  display: grid;
  grid-gap: 1em;
  grid-template-columns: 1fr;
  padding: 1.5em 1em;
  border-bottom: ${border};
`;

const Booking = styled.div`
  z-index: ${index10};
  display: flex;
  flex-direction: column;
`;

const BookingHeading = styled.h5`
  position: sticky;
  top: 0;
  z-index: ${index10};
  padding-bottom: 1.5em;
  background-color: ${white};
`;

const SlotsContainer = styled.div`
  max-height: 25em;
  padding: 0 1em;
  overflow-y: auto;
  border-left: ${border};
`;

const Slots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  margin-bottom: 8em;

  @media (min-width: 48em) {
    margin-bottom: initial;
  }
`;

const Detail = styled(FlexCenterVertical)`
  word-break: break-word;
`;

const Label = styled.h5`
  margin-left: 0.875em;
`;

const SlotButton = styled(Button)`
  &.active {
    border: 3px solid ${brand};

    span {
      color: ${brand};
    }

    &:hover {
      background-color: ${brandLight};
    }
  }
`;

const Page = styled.div`
  height: calc(100vh - 80px);
  margin-bottom: auto;

  .MuiButtonBase-root {
    border-radius: 10em;
  }

  @media (min-width: 30em) {
    height: auto;
    padding: 1.5em 0.5em 0 0.5em;
  }

  @media (min-width: 48em) {
    margin-bottom: initial;

    ${Booking} {
      flex-direction: row;
    }

    ${BookingHeading} {
      padding-top: 1.5em;
    }
  }
`;

const DialogActions = withStyles(() => ({
  root: {
    zIndex: 1000,
    position: 'fixed',
    height: '5em',
    bottom: 0,
    backgroundColor: white,
    padding: '0 1em',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: 'auto',
    borderTop: border,

    '@media (min-width: 30em)': {
      position: 'unset',
    },
  },
}))(MuiDialogActions);

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: ${grey50};
  }
`;

const Scheduler = ({
  organization,
  date,
  handleDateChange,
  selectedSlotIndex,
  setSelectedSlotIndex,
  schedulerId,
  selectLocation,
  selectService,
  setSelectedSlot,
  selectedDate,
  setSelectedDate,
  setStep,
  clinic,
  setClinic,
  subLocation,
  setSubLocation,
  service,
  subLocations,
}) => {
  useEffect(async () => {
    if (selectLocation && selectService && schedulerId) {
      const { data } = await getClinicSlots({
        sublocation_id: selectLocation,
        service_id: selectService.id,
        schedule_id: schedulerId,
      });

      setClinic(data);
    }
  }, [selectLocation, selectService, schedulerId]);

  useEffect(async () => {
    if (selectLocation) {
      setSubLocation(find(subLocations, ['id', selectLocation]));
    }
  }, [selectLocation]);

  if (isEmpty(clinic)) {
    return null;
  }

  const handlePrev = () => {
    setStep('service');
  };

  const handleNext = () => {
    setStep('confirm');
  };

  const combinedTimeSlots = clinic
    .map(({ id, times: timeSlots }) =>
      timeSlots.map((element) => ({ ...element, clinic_id: id })),
    )
    .reduce((acc, val) => acc.concat(val), []);

  const calendarSelectedDate = moment(date).format('YYYY-MM-DD');

  const { name, logo } = organization || {};

  const details = [
    {
      label: name,
      icon: <BusinessIcon />,
    },
    {
      label: selectService.name,
      icon: <HealthAndSafetyOutlinedIcon />,
    },
    {
      label: siteNameGenerator(organization, subLocation),
      icon: <RoomIcon />,
    },
  ];

  const timeSlots = groupBy(combinedTimeSlots, ({ start_date: startDate }) =>
    moment(startDate, 'YYYY-MM-DD hh:mm:ss').format('YYYY-MM-DD'),
  );

  console.log('selectedDate', selectedDate);

  return (
    <Page>
      <Logo src={GhsLogo} alt="GHS Logo" />

      <Content>
        {logo && <OrganizationLogo src={logo} alt="Organization Logo" />}

        <FormWrapper>
          <Details>
            {details.map(({ label, icon }) => (
              <Detail>
                {icon}
                <Label>{label}</Label>
              </Detail>
            ))}
          </Details>

          <Booking>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDatePicker
                orientation="landscape"
                openTo="day"
                value={date}
                onChange={handleDateChange}
                showToolbar={false}
                disablePast
                shouldDisableDate={(date) =>
                  !Object.keys(timeSlots).includes(
                    moment(date).format('YYYY-MM-DD'),
                  )
                }
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <SlotsContainer>
              <BookingHeading>
                {moment(date).format('dddd, MMMM DD, YYYY')}
              </BookingHeading>
              <Slots>
                {timeSlots[calendarSelectedDate] &&
                  timeSlots[calendarSelectedDate].map((slot, index) => {
                    const { start_date: startDate, count } = slot;

                    const formatTime = moment(
                      startDate,
                      'YYYY-MM-DD hh:mm:ss',
                    ).format('h:mma');

                    const sameDay =
                      moment(date).format('YYYY-MM-DD') ===
                      moment(selectedDate).format('YYYY-MM-DD');

                    const handleSelectSlot = () => {
                      setSelectedDate(date);
                      setSelectedSlot(slot);
                      setSelectedSlotIndex(index);
                    };

                    return (
                      <SlotButton
                        variant="outlined"
                        color="grey"
                        onClick={handleSelectSlot}
                        className={
                          sameDay && index === selectedSlotIndex && 'active'
                        }
                        disabled={count === 0}
                      >
                        {formatTime}
                      </SlotButton>
                    );
                  })}
              </Slots>
            </SlotsContainer>
          </Booking>

          <DialogActions>
            {handlePrev && (
              <BackButton onClick={handlePrev}>
                <ChevronLeftIcon fontSize="small" />
                Back
              </BackButton>
            )}

            <NextButton
              onClick={handleNext}
              className=""
              disabled={!selectedDate}
            >
              Next
            </NextButton>
          </DialogActions>
        </FormWrapper>
      </Content>
    </Page>
  );
};

export default Scheduler;
